import type { ActionContextPayload } from '@unifyapps/defs/hooks/useBlockEvents';
import type { DataSourceEntity } from '@unifyapps/defs/types/dataSource';
import { useTranslation } from '@unifyapps/i18n/client';
import { useQueryClient } from '@unifyapps/network/react-query';
import { useSnackbar } from '@unifyapps/ui/components/Snackbar';
import { useCallback } from 'react';
import useExecuteNode from '../../../../../components/Copilot/hooks/useExecuteNode';
import useMutationHelpers from '../../../../../hooks/useMutationHelpers';
import {
  fetchPlatformAutomationConfigLookup,
  PlatformAutomationConfigType,
} from '../../../../../platform-automation/hooks/useFetchPlatformAutomationConfig';
import { getTimezone } from '../../../../../utils/date';
import { getSelectedDataSourcePath } from '../../../../../utils/getSelectedDataSourcePath';
import type { DataSourceRecordStoreInstanceState } from '../../../../stores/DataSourceRecordStore/types';
import { useGetNoCodeComputedData } from '../../../computeContext/useGetNoCodeComputedData';
import type { ExportDataPayload } from './types';
import { getRecordsPathKey } from './utils';

export const useExportDataSource = () => {
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation(['blocks']);
  const { mutate } = useExecuteNode();
  const { getNoCodeComputedData } = useGetNoCodeComputedData();
  const queryClient = useQueryClient();

  const { onMutationError, onShowSuccessSnackbar } = useMutationHelpers();

  return useCallback(
    // dataSources is supplied by the caller (useExportData) so that resolution honors the propagated instanceId
    async (
      payload: ExportDataPayload,
      actionContext: ActionContextPayload,
      dataSources: DataSourceRecordStoreInstanceState['dataSources'],
    ) => {
      //   Fetch in the callback to prevent fetching it unless called upon
      const configLookupResponse = await fetchPlatformAutomationConfigLookup(
        queryClient,
        PlatformAutomationConfigType.ExportApplicationDatasource,
      );

      const config = configLookupResponse.config;

      if (!config) {
        showSnackbar({ color: 'error', title: t('blocks:Actions.ExportData.NoConfig') });
        return;
      }

      const { dataSourceId, pathArray } = getSelectedDataSourcePath({
        data: payload.data,
      });

      if (!dataSourceId) {
        showSnackbar({ color: 'error', title: t('blocks:Actions.ExportData.NoData') });
        return;
      }

      const dataSource = dataSources[dataSourceId];

      if (!dataSource) {
        showSnackbar({ color: 'error', title: t('blocks:Actions.ExportData.NoData') });
        return;
      }

      const computedDataSource = getNoCodeComputedData<DataSourceEntity>(
        dataSource as unknown as Record<string, unknown>,
        actionContext,
      );

      const parameters = {
        fileName: payload.fileName || 'Untitled',
        fileType: payload.fileType || 'CSV',
        sheetName: payload.sheetName || 'Sheet1',
        recordsPathKey: `${getRecordsPathKey(pathArray)}[*]`,
        timezone: getTimezone(),
        datasource: {
          id: dataSourceId,
          inputs: computedDataSource.properties.inputs,
          context: computedDataSource.properties.context,
          options: computedDataSource.properties.options,
        },
      };

      mutate(
        {
          synchronous: false,
          parameters,
          automationId: config.automationId,
        },
        {
          onError: onMutationError,
          onSuccess: () => {
            onShowSuccessSnackbar({
              title: t('blocks:Actions.ExportData.Success.Title'),
              description: t('blocks:Actions.ExportData.Success.Description'),
            });
          },
        },
      );
    },
    [
      getNoCodeComputedData,
      mutate,
      onMutationError,
      onShowSuccessSnackbar,
      queryClient,
      showSnackbar,
      t,
    ],
  );
};
